import { useCallback, useEffect, useRef } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useChatRail } from "./ChatRailProvider";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { ChatRailBody } from "./ChatRailBody";
import { RunDetailDrawer } from "../RunDetailDrawer/RunDetailDrawer";

const COLLAPSED_W = 56;

export function ChatRail() {
  const {
    available,
    collapsed,
    setCollapsed,
    widthPx,
    setWidthPx,
    isResizing,
    setIsResizing,
    MIN_W,
    MAX_W,
    drawerRunId,
    setDrawerRunId,
  } = useChatRail();
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  // Latest-callback refs let stable window listeners read fresh state
  // without forcing onPointerUp to reference itself in its own deps.
  const handlersRef = useRef({
    move: (_e: PointerEvent) => {},
    up: (_e: PointerEvent) => {},
  });
  const stableMoveRef = useRef((e: PointerEvent) => handlersRef.current.move(e));
  const stableUpRef = useRef((e: PointerEvent) => handlersRef.current.up(e));

  useEffect(() => {
    handlersRef.current.move = (e: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = dragRef.current.startX - e.clientX;
      setWidthPx(dragRef.current.startW + dx);
    };
    handlersRef.current.up = () => {
      dragRef.current = null;
      setIsResizing(false);
      window.removeEventListener("pointermove", stableMoveRef.current);
      window.removeEventListener("pointerup", stableUpRef.current);
    };
  }, [setWidthPx, setIsResizing]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startW: widthPx };
      setIsResizing(true);
      window.addEventListener("pointermove", stableMoveRef.current);
      window.addEventListener("pointerup", stableUpRef.current);
    },
    [widthPx, setIsResizing],
  );

  useEffect(
    () => () => {
      window.removeEventListener("pointermove", stableMoveRef.current);
      window.removeEventListener("pointerup", stableUpRef.current);
    },
    [],
  );

  if (!available) return null;

  if (collapsed) {
    return (
      <Box
        component="aside"
        sx={{
          position: "fixed",
          top: 64,
          right: 0,
          bottom: 0,
          width: COLLAPSED_W,
          bgcolor: T.rail,
          borderLeft: `1px solid ${T.hair}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 1,
          zIndex: (t) => t.zIndex.appBar - 1,
        }}
      >
        <Tooltip title="Open chat" placement="left">
          <IconButton size="small" onClick={() => setCollapsed(false)}>
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        <Box mt={1}>
          <ChatBubbleOutlineIcon sx={{ color: T.textMute }} />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        component="aside"
        sx={{
          position: "fixed",
          top: 64,
          right: 0,
          bottom: 0,
          width: widthPx,
          bgcolor: T.rail,
          borderLeft: `1px solid ${T.hair}`,
          display: "flex",
          flexDirection: "row",
          zIndex: (t) => t.zIndex.appBar - 1,
          minWidth: MIN_W,
          maxWidth: MAX_W,
          userSelect: isResizing ? "none" : undefined,
        }}
      >
        {/* Drag handle */}
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat rail"
          onPointerDown={onPointerDown}
          sx={{
            width: T.drag_handle_w,
            cursor: "ew-resize",
            bgcolor: isResizing ? T.accentBorder : "transparent",
            transition: "background-color 120ms ease",
            "&:hover": { bgcolor: T.accentBorder },
            flexShrink: 0,
          }}
        />

        <ChatRailBody
          trailingHeader={
            <Tooltip title="Collapse chat" placement="left">
              <IconButton size="small" onClick={() => setCollapsed(true)}>
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
          }
        />
      </Box>

      {drawerRunId && <RunDetailDrawer runId={drawerRunId} onClose={() => setDrawerRunId(null)} />}
    </>
  );
}
